import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';
import { Input, Label, Text } from 'components';
import { theme, toRem } from 'design';

interface IHoldbackProps {
  hasHoldback?: boolean;
  noteVisible?: boolean;
}

const BREAKPOINT_NH = {
  layoutShift1: 1024,
  layoutShift2: 1150,
};

// Item
export const Layout = styled.li`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  border-radius: ${toRem(4)};
  overflow-y: visible;
  margin: 0 0 ${toRem(16)} 0;
  background-color: ${theme.color.neutral[50]};
`;

// Item Header
export const Header = styled.header`
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: ${toRem(66)};
  padding: ${toRem(22)} ${toRem(22)} ${toRem(11)} ${toRem(22)};
`;

export const HeaderSection = styled.section`
  align-items: center;
  height: 100%;

  &.user-info {
    flex-grow: 1;
    min-width: ${toRem(220)};
    flex-basis: ${toRem(360)};
    word-break: break-word;
  }

  &.payment-input-section {
    justify-content: center;
    flex-basis: ${toRem(360)};
  }

  &:last-child {
    justify-content: flex-end;
    flex-basis: ${toRem(100)};
    flex-shrink: 4;

    > * {
      margin-left: ${toRem(8)};

      &:first-child {
        margin-left: 0;
      }
    }
  }
`;

// User Info
export const UserInfoLayout = styled.div`
  flex-grow: 1;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  height: auto;
`;

export const UserInfo = styled.section`
  flex-grow: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  margin: 0 0 0 ${toRem(11)};

  > *:not(:last-child) {
    margin-bottom: ${toRem(3)};

    @media (min-width: ${theme.breakpoint.small}px) {
      margin-bottom: ${toRem(6)};
    }
  }
`;

export const Name = styled(Text).attrs({
  size: 'small',
})`
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(14)};
  line-height: 100%;
  color: ${theme.color.neutral[900]};
`;

export const IconButton = styled.button.attrs({
  type: 'button',
})`
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  width: ${toRem(24)};
  height: ${toRem(24)};
  background-color: ${theme.color.white};
  border-radius: 50%;
  overflow: hidden;
  border: ${toRem(1)} solid ${theme.color.neutral[200]};

  svg {
    fill: ${theme.color.neutral[800]};
  }

  &:hover,
  &:focus {
    background-color: ${theme.color.neutral[100]};
    svg {
      fill: ${theme.color.neutral[900]};
    }
  }

  &.note-visible {
    background-color: ${theme.color.neutral[900]};
    svg {
      fill: ${theme.color.white};
    }
  }
`;

// Bottom
export const Bottom = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  width: 100%;
  height: auto;
  padding: 0 ${toRem(22)};
`;

export const InputForm = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;

  @media (min-width: ${BREAKPOINT_NH.layoutShift1}px) {
    display: flex;
  }

  @media (min-width: ${BREAKPOINT_NH.layoutShift2}px) {
    display: none;
  }
`;

export const DesktopInputContainer = styled.div<IHoldbackProps>`
  ${({ hasHoldback }) =>
    hasHoldback &&
    css`
      > * {
        max-width: ${toRem(150)};
        margin-right: ${toRem(16)};

        &:last-child {
          width: auto;
          padding-right: 0;
        }
      }

      input {
        max-width: ${toRem(125)};
      }
    `}
`;

// Inputs
export const FormLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;

  > * {
    margin-bottom: ${toRem(16)};
  }
`;

export const CurrencyInputContainer = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;

  > div,
  > p {
    margin-top: ${toRem(5.5)};
  }

  p {
    display: flex;
    flex-flow: row nowrap;
    width: 100%;
    text-align: left;
  }
`;

// Note
export const Note = styled(motion.section)<IHoldbackProps>`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  border-top: 1px solid ${theme.color.neutral[200]};
  width: 100%;
  height: auto;
  overflow: hidden;
  padding-top: ${toRem(12)};
  visibility: ${({ noteVisible }) => (noteVisible ? 'visible' : 'hidden')};

  ${({ hasHoldback, noteVisible }) =>
    hasHoldback &&
    noteVisible &&
    css`
      margin-bottom: ${toRem(11)};
    `}

  ${({ hasHoldback, noteVisible }) =>
    !hasHoldback &&
    noteVisible &&
    css`
      margin-bottom: ${toRem(11)};
    `}
`;

export const NoteInput = styled(Input)<IHoldbackProps>`
  flex-shrink: 0;
`;

export const DesktopCurrencyInputContainer = styled(CurrencyInputContainer)`
  display: none;

  @media (min-width: ${BREAKPOINT_NH.layoutShift1}px) {
    display: none;
  }

  @media (min-width: ${BREAKPOINT_NH.layoutShift2}px) {
    display: flex;
    width: ${toRem(100)};
  }
`;

export const TopLabel = styled(Label)`
  font-size: ${toRem(11)};
`;

export const NetLabelWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${toRem(theme.input.height.large)};
  width: ${toRem(80)};
`;

export const NetLabel = styled(Label)`
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(16)};
  width: ${toRem(80)};
`;
